import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { OverlayTrigger, Popover } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { apiService } from '../../services/api.service'
import { Document as DocumentType } from '../../types'
import './PreviewPopup.scss'

interface PreviewPopupProps {
  document: DocumentType
  linkClasses?: string
  linkTitle?: string
  children: React.ReactNode
}

export interface PreviewPopupRef {
  close: (immediate?: boolean) => void
}

const PreviewPopup = forwardRef<PreviewPopupRef, PreviewPopupProps>(({ document, linkClasses, children }, ref) => {
  const { t } = useTranslation()
  const [showPopover, setShowPopover] = useState(false)
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [mouseOnPreview, setMouseOnPreview] = useState(false)
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const mouseOnPopoverRef = useRef(false)

  useEffect(() => {
    if (!showPopover || blobUrl) return

    setLoading(true)
    apiService
      .get(`documents/${document.id}/preview/`, { responseType: 'blob' })
      .then((res) => {
        const blob = res.data as Blob
        const url = URL.createObjectURL(blob)
        setBlobUrl(url)
        setLoading(false)
      })
      .catch((_error) => {
        setLoading(false)
      })

    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl)
      }
    }
  }, [showPopover, document.id, blobUrl])

  useEffect(() => {
    if (mouseOnPreview && !showPopover) {
      setShowPopover(true)
    }
    if (!showPopover) {
      mouseOnPopoverRef.current = false
    }
  }, [mouseOnPreview, showPopover])

  const closePopup = (immediate: boolean = false) => {
    setTimeout(
      () => {
        if (!mouseOnPopoverRef.current) {
          setShowPopover(false)
        }
      },
      immediate ? 0 : 100
    )
  }

  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setMouseOnPreview(true)
    }, 500)
  }

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
      hoverTimeoutRef.current = null
    }
    setMouseOnPreview(false)
    if (showPopover && !mouseOnPopoverRef.current) {
      setTimeout(() => {
        if (!mouseOnPopoverRef.current) {
          setShowPopover(false)
        }
      }, 100)
    }
  }

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current)
      }
    }
  }, [])

  useImperativeHandle(ref, () => ({
    close: () => closePopup(),
  }))

  const popover = (
    <Popover
      id={`preview-popover-${document.id}`}
      className="shadow popover-preview"
      onMouseEnter={() => {
        mouseOnPopoverRef.current = true
        setMouseOnPreview(true)
      }}
      onMouseLeave={() => {
        mouseOnPopoverRef.current = false
        setMouseOnPreview(false)
        closePopup()
      }}
    >
      <Popover.Header
        as="h3"
        className="d-flex align-items-center justify-content-between bg-dark text-white px-3 py-2 border-0"
        style={{ margin: 0, fontSize: '1rem', fontWeight: 500 }}
      >
        <span className="text-truncate me-2" style={{ maxWidth: '90%' }}>
          {document.title || document.original_file_name || t('preview_document')}
        </span>
        <i className="bi bi-file-earmark-text" style={{ fontSize: '1.2rem' }}></i>
      </Popover.Header>
      <Popover.Body
        onMouseEnter={() => {
          mouseOnPopoverRef.current = true
          setMouseOnPreview(true)
        }}
        onMouseLeave={() => {
          mouseOnPopoverRef.current = false
          setMouseOnPreview(false)
          closePopup()
        }}
      >
        <div
          className="preview-popup-container"
          onMouseEnter={() => {
            mouseOnPopoverRef.current = true
            setMouseOnPreview(true)
          }}
          onMouseLeave={() => {
            mouseOnPopoverRef.current = false
            setMouseOnPreview(false)
            closePopup()
          }}
        >
          {loading ? (
            <div className="w-100 h-100 position-relative d-flex justify-content-center align-items-center">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : blobUrl ? (
            <div
              style={{ width: '100%', height: '100%', position: 'relative' }}
              onMouseEnter={() => {
                mouseOnPopoverRef.current = true
                setMouseOnPreview(true)
              }}
              onMouseLeave={() => {
                mouseOnPopoverRef.current = false
                setMouseOnPreview(false)
                closePopup()
              }}
            >
              <iframe src={blobUrl} width="100%" height="100%" style={{ border: 'none', display: 'block' }} title={t('preview_document')} />
            </div>
          ) : (
            <div className="w-100 h-100 position-relative d-flex justify-content-center align-items-center">
              <p className="text-muted">No preview available</p>
            </div>
          )}
        </div>
      </Popover.Body>
    </Popover>
  )

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    const previewUrl = `${window.location.origin}/documents/${document.id}/preview`
    window.open(previewUrl, '_blank')
  }

  const shouldWrapInButton = linkClasses && React.Children.count(children) === 1

  const triggerElement = shouldWrapInButton ? (
    <button type="button" className={linkClasses} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} onClick={handleClick}>
      {children}
    </button>
  ) : (
    <span
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      style={{ display: 'inline-block', cursor: 'pointer' }}
    >
      {children}
    </span>
  )

  return (
    <OverlayTrigger
      trigger={[]}
      placement="auto"
      overlay={popover}
      show={showPopover}
      rootClose={true}
      delay={{ show: 0, hide: 200 }}
      onToggle={(nextShow) => {
        if (!nextShow) {
          setShowPopover(false)
        }
      }}
    >
      {triggerElement}
    </OverlayTrigger>
  )
})

PreviewPopup.displayName = 'PreviewPopup'

export default PreviewPopup
